import { Card } from 'primereact/card';
import { useEffect, useTransition } from 'react';
import { endOfWeek, format, startOfWeek } from 'date-fns';
import { PreloadedQuery, useLazyLoadQuery, useQueryLoader } from 'react-relay';
import { getGoalMetWeekly, getWeeklySnapshotInfo } from '../../graphQLOperations/Dashboard';
import { useAppSelector } from '../../redux/hooks';
import { DashboardGoalMetByWeekQuery } from '../../graphQLOperations/__generated__/DashboardGoalMetByWeekQuery.graphql';
import WeeklyHistory from './WeeklyHistory/WeeklyHistory';
import SkillChallengeAttemptsWithoutPassing from './SkillChallengeAttemptsWithoutPassing';
import { AttemptsWithoutPassingQuery } from '../../graphQLOperations/__generated__/AttemptsWithoutPassingQuery.graphql';
import { attemptsWithoutPassing } from '../../graphQLOperations/AttemptsWithoutPassing';
import GoalMet from './GoalMet/GoalMet';
import WeeklyMeterGroup from './WeeklyMeterGroup';
import { DashboardGetWeeklySnapshotQuery } from '../../graphQLOperations/__generated__/DashboardGetWeeklySnapshotQuery.graphql';
import { ThreeDotLoader } from '../Fallback/ThreeDotsLoader';
import { selectUser } from '../../redux/reducers/userSlice';
import { CustomBranding, mapCustomBranding, Roles } from '../../models/User';
import TeacherDashboardInfoIcon, { InfoType } from './TeacherDashboardInfoIcon';
import { EmptyResultsWidgets, FilterOptionValues } from './TeacherDashboard';

export interface WeeklySnapshotQueryFilters {
  classIds: string[] | null;
  targetGroupIds: string[] | null;
  siteIds: string[] | null;
  gradeLevelIds: string[] | null;
  reportFromDate: Date;
  reportToDate: Date;
}

interface Props {
  queryFilters: WeeklySnapshotQueryFilters;
  filterOption: FilterOptionValues;
  setEmptyResult: React.Dispatch<React.SetStateAction<EmptyResultsWidgets>>;
}

export const WeeklySnapshot = ({ queryFilters, filterOption, setEmptyResult }: Props) => {
  const userRedux = useAppSelector(selectUser);
  const miamiBrand =
    userRedux?.network?.customBranding &&
    mapCustomBranding[userRedux?.network?.customBranding] === CustomBranding.MIAMI;

  const [isPending, startTransition] = useTransition();
  const startDate = startOfWeek(new Date(), { weekStartsOn: 1 });
  const endDate = endOfWeek(new Date(), { weekStartsOn: 1 });

  const [queryReference, loadQuery] = useQueryLoader<AttemptsWithoutPassingQuery>(attemptsWithoutPassing);
  const [queryReferenceWeeklySnapshot, loadQueryWeeklySnapshot] =
    useQueryLoader<DashboardGetWeeklySnapshotQuery>(getWeeklySnapshotInfo);

  const isTeacher =
    userRedux?.roles.some(role => role.name.toLocaleLowerCase() === Roles.TEACHER) &&
    !userRedux?.roles.find(role => role.name.toLocaleLowerCase() === Roles.NETWORK_ADMIN) &&
    !userRedux?.roles.find(role => role.name.toLocaleLowerCase() === Roles.SITE_ADMIN);

  useEffect(() => {
    if (!isTeacher || (isTeacher && queryFilters.classIds)) {
      loadQuery({ filter: queryFilters }, { fetchPolicy: 'store-and-network' });
      startTransition(() => {
        loadQueryWeeklySnapshot({ filter: queryFilters }, { fetchPolicy: 'store-and-network' });
      });
    }
  }, [queryFilters]);

  const { studentSkillGoalsReportByWeek } = useLazyLoadQuery<DashboardGoalMetByWeekQuery>(getGoalMetWeekly, {
    filter: {
      dateRanges: [{ fromDate: queryFilters.reportFromDate, toDate: queryFilters.reportToDate }],
      classIds: queryFilters.classIds,
      siteIds: queryFilters.siteIds,
      gradeLevelIds: queryFilters.gradeLevelIds,
      passedSkillGoal: 2,
    },
  });

  return (
    <Card
      header={
        <div className="w-12 flex justify-content-between align-items-center sm:flex-row flex-column">
          <div>
            <h1 className="inline text-lg md:text-2xl mr-2">
              {miamiBrand ? 'Progress towards Skill Goal' : 'Weekly Snapshot'}
            </h1>{' '}
            <p className="inline text-base font-sans">
              {format(startDate, 'P')} - {format(endDate, 'P')}
            </p>
          </div>
          {miamiBrand && <TeacherDashboardInfoIcon infoType={InfoType.WEEKLY} />}
        </div>
      }
      className="col-12 p-4 gap-0"
    >
      {queryReferenceWeeklySnapshot && !isPending ? (
        <WeeklyMeterGroup
          filterOption={filterOption}
          queryReference={
            queryReferenceWeeklySnapshot as PreloadedQuery<DashboardGetWeeklySnapshotQuery, Record<string, unknown>>
          }
          setEmptyResult={setEmptyResult}
        />
      ) : (
        <ThreeDotLoader dark />
      )}

      <div className="w-12">
        <h3 className="text-center md:text-lg text-base">
          <span className="font-bold">Goal:</span> Each student will pass 2 new skills per week.
        </h3>
        <div className="flex lg:flex-row flex-column mt-3">
          <div className="lg:col-4 lg:flex-column lg:gap-0 sm:col-12 sm:flex sm:flex-row sm:align-items-end sm:gap-2">
            <GoalMet
              percentage={studentSkillGoalsReportByWeek[0].percentageStudentsPassedGoal}
              queryFilters={queryFilters}
            />

            <WeeklyHistory queryFilters={queryFilters} />
          </div>
          {queryReference && (
            <SkillChallengeAttemptsWithoutPassing
              queryReference={queryReference as PreloadedQuery<AttemptsWithoutPassingQuery, Record<string, unknown>>}
            />
          )}
        </div>
      </div>
    </Card>
  );
};
